import React, { useRef } from 'react';
import { Box, CircularProgress, Divider } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOnViewport } from '../../../lib/hooks';
import useSearchService from '../../../services/SearchService';
import { USER_SEARCH_EVENTS } from '../../../lib/queryKeys';
import { SttEventCard } from './SttEventCard';

type Props = {
    params?: Record<string, unknown>;
};

/**
 * List of activities cards. Use the infinite scroll.
 * @param params Parameters that are sent to the API.
 * @returns {React.ReactElement}
 */
export function SttEventCardList({ params = {} }: Props): React.ReactElement {
    const ROWS_PER_PAGE = 15;
    const { t } = useTranslation();
    const searchService = useSearchService();
    const ref = useRef<HTMLDivElement>(null);
    const { data, status, isFetchingNextPage, fetchNextPage, hasNextPage } =
        useInfiniteQuery({
            queryKey: [
                USER_SEARCH_EVENTS,
                {
                    ...params,
                },
            ],
            queryFn: ({ pageParam = 1 }: any) =>
                searchService
                    .getList({
                        page: pageParam,
                        rows: ROWS_PER_PAGE,
                        kind: 2,
                        ...params,
                    })
                    .then((response: any) => {
                        response.events.page = pageParam;
                        return response;
                    }),
            getNextPageParam: (lastPage: any) =>
                lastPage?.events?.page < lastPage?.events?.pages
                    ? lastPage?.events?.page + 1
                    : undefined,
            initialPageParam: 1,
        });
    useOnViewport(ref as any, '-100px', fetchNextPage);

    return (
        <>
            {status === 'pending' ? (
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            ) : status === 'error' ? (
                <>Error</>
            ) : (
                <>
                    <Box my={3} sx={{ textAlign: 'right' }}>
                        <b>{data?.pages[0]?.events?.count}</b> {t('events')}
                    </Box>
                    {data?.pages.map((page: any, i: number) => (
                        <React.Fragment key={i}>
                            {page.events.rows.map(
                                (event: unknown, eventKey: number) => (
                                    <Box key={eventKey}>
                                        {eventKey > 0 && (
                                            <Box mt={1} mb={1}>
                                                <Divider />
                                            </Box>
                                        )}
                                        <SttEventCard event={event as any} />
                                    </Box>
                                ),
                            )}
                        </React.Fragment>
                    ))}
                    {isFetchingNextPage && (
                        <Box display="flex" justifyContent="center">
                            <CircularProgress />
                        </Box>
                    )}
                    {!hasNextPage && (
                        <Box display="flex" justifyContent="center">
                            No hay más resultados.
                        </Box>
                    )}
                </>
            )}
            {/* Este div es para detectar el final de la página, cuando sea visible se van a pedir datos a la API para realizar el scroll infinito. */}
            <div ref={ref} />
        </>
    );
}
